import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ShoppingCartComponent from "@/components/agency-solutions/ShoppingCart";
import SolutionsHeader from "@/components/agency-solutions/SolutionsHeader";
import SolutionsContent from "@/components/agency-solutions/SolutionsContent";
import { solutionsData } from "@/data/solutionsData";
import { PRICE_FACTORS, Solution } from "@/types/solutions";

// CURT solutions data to include in the marketplace
const curtSolutionsData: Solution[] = [
  {
    id: "curt-001",
    title: "Modular Construction Implementation Guide",
    agency: "CURT", // CURT channel
    curator: "Karen Watson",
    commercialType: "Sale",
    category: "Construction Methods",
    basePrice: 3500,
    price: 3500,
    description: "Comprehensive guide for implementing modular construction techniques in commercial projects.",
    contentType: "Public"
  },
  {
    id: "curt-002", 
    title: "Prefabrication Best Practices Framework",
    agency: "CURT",
    curator: "Jim Lawnin",
    commercialType: "Lease",
    category: "Construction Methods", 
    basePrice: 2800,
    price: 2800,
    description: "Structured framework for prefabrication implementation with case studies and ROI analysis.",
    contentType: "Private"
  },
  {
    id: "curt-003",
    title: "Construction Safety Protocol System",
    agency: "CURT",
    curator: "Jess Simm", 
    commercialType: "Collaborate",
    category: "Health & Safety",
    basePrice: 1950,
    price: 1950,
    description: "Comprehensive safety protocols for modern construction environments.",
    contentType: "Public"
  },
  {
    id: "curt-004",
    title: "Project Management Excellence Framework",
    agency: "CURT",
    curator: "Mike Mosher",
    commercialType: "Sale", 
    category: "Project Management",
    basePrice: 4200,
    price: 4200,
    description: "Specialized knowledge base for construction project management optimization.",
    contentType: "Private"
  },
  {
    id: "curt-005",
    title: "Green Building Implementation System",
    agency: "CURT",
    curator: "Morgan Eldred, PhD",
    commercialType: "Stake",
    category: "Green Building", 
    basePrice: 5800,
    price: 5800,
    description: "Complete system for implementing sustainable practices in construction projects.",
    contentType: "Private"
  },
  {
    id: "curt-006",
    title: "BIM Integration Toolkit",
    agency: "CURT",
    curator: "Trey Fleming",
    commercialType: "Lease",
    category: "Automation",
    basePrice: 3200,
    price: 3200, 
    description: "Tools and methodologies for seamless BIM integration in construction workflows.",
    contentType: "Public"
  },
  {
    id: "curt-007",
    title: "Engineering & Design Standards",
    agency: "CURT",
    curator: "Karen Watson",
    commercialType: "Sale",
    category: "Engineering & Design",
    basePrice: 2950,
    price: 2950,
    description: "Comprehensive design standards and engineering best practices for construction.",
    contentType: "Private"
  },
  {
    id: "curt-008",
    title: "Construction Data Analytics Platform",
    agency: "CURT", 
    curator: "Jim Lawnin",
    commercialType: "Collaborate",
    category: "Data Analytics",
    basePrice: 4500,
    price: 4500,
    description: "Advanced analytics platform for construction project data optimization.",
    contentType: "Private"
  }
];

// iSPAI-specific solutions data from profile solution bounties
const ispaiSpecificSolutions: Solution[] = [
  // Trey Fleming solutions
  {
    id: "ispai-trey-001",
    title: "iSPAI Founding Vision and Concept",
    agency: "iSPAI",
    curator: "Trey Fleming",
    commercialType: "Sale",
    category: "Strategic Planning",
    basePrice: 45000,
    price: 45000,
    description: "Comprehensive founding vision and strategic concept framework for building intelligent strategic public AI initiatives.",
    contentType: "Private"
  },
  {
    id: "ispai-trey-002",
    title: "iSPAI Initial Seed Funding Strategy",
    agency: "iSPAI",
    curator: "Trey Fleming",
    commercialType: "Collaborate",
    category: "Financial Strategy",
    basePrice: 35000,
    price: 35000,
    description: "Strategic framework for securing initial seed funding and financial structuring for AI initiatives.",
    contentType: "Private"
  },
  {
    id: "ispai-trey-003",
    title: "iSPAI Founding Team Assembly Framework",
    agency: "iSPAI",
    curator: "Trey Fleming",
    commercialType: "Sale",
    category: "Team Building",
    basePrice: 30000,
    price: 30000,
    description: "Comprehensive framework for assembling and managing founding teams for AI-driven organizations.",
    contentType: "Public"
  },
  // Shaan Ray solutions
  {
    id: "ispai-shaan-001",
    title: "iSPAI Web3 Strategy Framework",
    agency: "iSPAI",
    curator: "Shaan Ray",
    commercialType: "Sale",
    category: "Web3 Strategy",
    basePrice: 40000,
    price: 40000,
    description: "Comprehensive Web3 strategy framework for implementing blockchain and decentralized technologies in AI platforms.",
    contentType: "Private"
  },
  {
    id: "ispai-shaan-002",
    title: "CURT POC Blockchain Feasibility Report",
    agency: "iSPAI",
    curator: "Shaan Ray",
    commercialType: "Collaborate",
    category: "Blockchain Analysis",
    basePrice: 25000,
    price: 25000,
    description: "Detailed blockchain feasibility analysis and proof-of-concept report for construction industry applications.",
    contentType: "Private"
  },
  {
    id: "ispai-shaan-003",
    title: "iSPAI Web3 Components Product Suite",
    agency: "iSPAI",
    curator: "Shaan Ray",
    commercialType: "Sale",
    category: "Product Development",
    basePrice: 35000,
    price: 35000,
    description: "Modular Web3 components and product suite for building decentralized AI applications.",
    contentType: "Public"
  },
  {
    id: "ispai-shaan-004",
    title: "iSPAI Token Economics Framework",
    agency: "iSPAI",
    curator: "Shaan Ray",
    commercialType: "Stake",
    category: "Tokenomics",
    basePrice: 50000,
    price: 50000,
    description: "Advanced token economics framework and implementation guide for AI platform monetization.",
    contentType: "Private"
  },
  // Morgan Eldred solutions
  {
    id: "ispai-morgan-001",
    title: "iSPAI Information Management Framework",
    agency: "iSPAI",
    curator: "Morgan Eldred, PhD",
    commercialType: "Sale",
    category: "Information Management",
    basePrice: 42000,
    price: 42000,
    description: "Enterprise-grade information management framework for AI-driven knowledge platforms.",
    contentType: "Private"
  },
  {
    id: "ispai-morgan-002",
    title: "iSPAI Member IP Protection Strategy",
    agency: "iSPAI",
    curator: "Morgan Eldred, PhD",
    commercialType: "Collaborate",
    category: "Intellectual Property",
    basePrice: 38000,
    price: 38000,
    description: "Comprehensive intellectual property protection strategy for platform members and contributors.",
    contentType: "Private"
  },
  {
    id: "ispai-morgan-003",
    title: "Enterprise AI Integration Roadmap",
    agency: "iSPAI",
    curator: "Morgan Eldred, PhD",
    commercialType: "Sale",
    category: "AI Integration",
    basePrice: 45000,
    price: 45000,
    description: "Strategic roadmap for enterprise AI integration with practical implementation guidelines.",
    contentType: "Public"
  },
  // Mike Mosher solutions
  {
    id: "ispai-mike-001",
    title: "iSPAI Security and Privacy Framework",
    agency: "iSPAI",
    curator: "Mike Mosher",
    commercialType: "Sale",
    category: "Security",
    basePrice: 40000,
    price: 40000,
    description: "Comprehensive security and privacy framework for AI platforms with enterprise-grade protection.",
    contentType: "Private"
  },
  {
    id: "ispai-mike-002",
    title: "iSPAI Enterprise CISO Guidelines",
    agency: "iSPAI",
    curator: "Mike Mosher",
    commercialType: "Collaborate",
    category: "Security Management",
    basePrice: 35000,
    price: 35000,
    description: "Essential CISO guidelines and FAQs for enterprise adoption of AI platforms.",
    contentType: "Private"
  },
  {
    id: "ispai-mike-003",
    title: "AI Platform Security Architecture Implementation",
    agency: "iSPAI",
    curator: "Mike Mosher",
    commercialType: "Sale",
    category: "Security Architecture",
    basePrice: 32000,
    price: 32000,
    description: "Implementation plan and architecture for securing AI platforms and data infrastructure.",
    contentType: "Public"
  },
  {
    id: "ispai-mike-004",
    title: "iSPAI GRC Agentic Automation Model",
    agency: "iSPAI",
    curator: "Mike Mosher",
    commercialType: "Stake",
    category: "Governance",
    basePrice: 28000,
    price: 28000,
    description: "Governance, risk, and compliance automation model using agentic AI approaches.",
    contentType: "Private"
  }
];

// Combine all data sources
const allSolutionsData = [...solutionsData, ...curtSolutionsData, ...ispaiSpecificSolutions];
import { getAgencyColor, getCommercialTypeColor } from "@/utils/solutionUtils";

const IspaiMarketplace = () => {
  const [searchParams] = useSearchParams();
  const [selectedAgency, setSelectedAgency] = useState<string>("all");
  const [commercialType, setCommercialType] = useState<string>("Sale");
  const [contentType, setContentType] = useState<string>("all");
  const [cart, setCart] = useState<Solution[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("list");
  const [solutions, setSolutions] = useState<Solution[]>([]);
  
  // New filter states for column-based filtering
  const [selectedAgencies, setSelectedAgencies] = useState<string[]>([]);
  const [selectedCreators, setSelectedCreators] = useState<string[]>([]);
  const [selectedCommercialTypes, setSelectedCommercialTypes] = useState<string[]>([]);
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([]);
  const [solutionSearchQuery, setSolutionSearchQuery] = useState("");
  const [priceSort, setPriceSort] = useState("");

  // Initialize search query from URL parameters
  useEffect(() => {
    const urlSearchQuery = searchParams.get('search');
    if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
    }
  }, [searchParams]);

  // Update prices when commercial type changes
  useEffect(() => {
    const updatedSolutions = allSolutionsData.map(solution => ({
      ...solution,
      commercialType: commercialType as "Sale" | "Lease" | "Collaborate" | "Stake",
      price: Math.round(solution.basePrice * PRICE_FACTORS[commercialType as keyof typeof PRICE_FACTORS])
    }));
    setSolutions(updatedSolutions);
  }, [commercialType]);

  // Initialize solutions on component mount
  useEffect(() => {
    setSolutions(allSolutionsData);
  }, []);

  // Filter and sort solutions based on various criteria
  const filteredAndSortedSolutions = solutions
    .filter(solution => {
      // Legacy filters
      const agencyMatch = selectedAgency === "all" || solution.agency === selectedAgency;
      const contentMatch = contentType === "all" || solution.contentType === contentType;
      const searchMatch = searchQuery === "" || 
        solution.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.agency.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.curator.toLowerCase().includes(searchQuery.toLowerCase());

      // New column-based filters
      const agencyFilterMatch = selectedAgencies.length === 0 || selectedAgencies.includes(solution.agency);
      const creatorFilterMatch = selectedCreators.length === 0 || selectedCreators.includes(solution.curator);
      const commercialTypeFilterMatch = selectedCommercialTypes.length === 0 || selectedCommercialTypes.includes(solution.commercialType);
      const contentTypeFilterMatch = selectedContentTypes.length === 0 || selectedContentTypes.includes(solution.contentType);
      const solutionSearchMatch = solutionSearchQuery === "" || 
        solution.title.toLowerCase().includes(solutionSearchQuery.toLowerCase()) ||
        solution.description.toLowerCase().includes(solutionSearchQuery.toLowerCase()) ||
        solution.curator.toLowerCase().includes(solutionSearchQuery.toLowerCase());

      return agencyMatch && contentMatch && searchMatch && 
             agencyFilterMatch && creatorFilterMatch && commercialTypeFilterMatch && 
             contentTypeFilterMatch && solutionSearchMatch;
    })
    .sort((a, b) => {
      if (priceSort === "asc") return a.price - b.price;
      if (priceSort === "desc") return b.price - a.price;
      return 0;
    });

  // Cart management functions
  const addToCart = (solution: Solution) => {
    if (!cart.find(item => item.id === solution.id)) {
      setCart([...cart, solution]);
    }
  };

  const removeFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Marketplace</h1>
          <p className="text-lg text-gray-600">
            Discover solutions from all channels in the iSPAI ecosystem
          </p>
        </div>

        <SolutionsHeader
          cartItemCount={cart.length}
        />

        <div className="flex gap-6">
          <div className="flex-1">
            <SolutionsContent
              solutions={filteredAndSortedSolutions}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              viewType={viewType}
              setViewType={setViewType}
              addToCart={addToCart}
              cart={cart}
              getAgencyColor={getAgencyColor}
              getCommercialTypeColor={getCommercialTypeColor}
              selectedAgencies={selectedAgencies}
              setSelectedAgencies={setSelectedAgencies}
              selectedCreators={selectedCreators}
              setSelectedCreators={setSelectedCreators}
              selectedCommercialTypes={selectedCommercialTypes}
              setSelectedCommercialTypes={setSelectedCommercialTypes}
              selectedContentTypes={selectedContentTypes}
              setSelectedContentTypes={setSelectedContentTypes}
              solutionSearchQuery={solutionSearchQuery}
              setSolutionSearchQuery={setSolutionSearchQuery}
              priceSort={priceSort}
              setPriceSort={setPriceSort}
            />
          </div>

          <div className="w-80">
            <ShoppingCartComponent
              cart={cart}
              removeFromCart={removeFromCart}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IspaiMarketplace;